import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2, FileText, X, Download } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { normalizeWhatsApp } from "@/lib/whatsapp";
import { BRAND } from "@/brand";

const rupee = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Quotation builder + list, rendered inside the client timeline modal.
 * Shop worker picks products from the master, sets qty/discount, saves → gets a PDF + WhatsApp share.
 */
export default function QuoteBuilder({ submission, onClose }) {
  const [products, setProducts] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [items, setItems] = useState([]); // [{ product_id, quantity, price_override }]
  const [discount, setDiscount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("new"); // 'new' | 'list'

  useEffect(() => {
    (async () => {
      try {
        const [p, q] = await Promise.all([
          api.get("/products"),
          api.get(`/quotations/by-submission/${submission.id}`),
        ]);
        setProducts(p.data);
        setQuotes(q.data);
        if (q.data.length > 0) setTab("list");
      } catch (e) { toast.error(formatError(e)); }
      finally { setLoading(false); }
    })();
  }, [submission.id]);

  const addItem = () => setItems((arr) => [...arr, { product_id: "", quantity: 1, price_override: "" }]);
  const removeItem = (idx) => setItems((arr) => arr.filter((_, i) => i !== idx));
  const updateItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const totals = useMemo(() => {
    let sub = 0, gst = 0;
    for (const it of items) {
      const p = products.find((x) => x.id === it.product_id);
      if (!p) continue;
      const qty = parseFloat(it.quantity) || 0;
      const price = it.price_override !== "" && it.price_override != null ? parseFloat(it.price_override) : p.price;
      const line = qty * price;
      sub += line;
      gst += line * (p.gst_percent / 100);
    }
    const disc = sub * ((parseFloat(discount) || 0) / 100);
    const taxable = sub - disc;
    const gstScaled = sub > 0 ? gst * (taxable / sub) : 0;
    return { sub, disc, taxable, gst: gstScaled, grand: taxable + gstScaled };
  }, [items, discount, products]);

  const save = async () => {
    const validItems = items.filter((it) => it.product_id && parseFloat(it.quantity) > 0);
    if (validItems.length === 0) { toast.error("Add at least one product"); return; }
    setSaving(true);
    try {
      const payload = {
        submission_id: submission.id,
        discount_percent: parseFloat(discount) || 0,
        notes: notes.trim() || null,
        items: validItems.map((it) => ({
          product_id: it.product_id,
          quantity: parseFloat(it.quantity),
          price_override: it.price_override !== "" && it.price_override != null ? parseFloat(it.price_override) : null,
        })),
      };
      const { data } = await api.post("/quotations", payload);
      toast.success(`Quote ${data.quote_no} created`);
      setQuotes((prev) => [data, ...prev]);
      setItems([]); setDiscount(""); setNotes("");
      setTab("list");
    } catch (e) { toast.error(formatError(e)); }
    finally { setSaving(false); }
  };

  const downloadPdf = async (q) => {
    try {
      const res = await api.get(`/quotations/${q.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${q.quote_no.replace(/\//g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e)); }
  };

  const openWhatsApp = (q) => {
    const num = normalizeWhatsApp(submission.client_mobile);
    if (!num) { toast.error("Client mobile not valid for WhatsApp"); return; }
    const msg = `Hello ${submission.client_name || "there"},\n\nPlease find your quotation *${q.quote_no}* from *${BRAND.companyName}*.\n\nGrand Total: ₹${q.grand_total.toLocaleString("en-IN")}\n\nWe will share the PDF right after this message.\n\nThank you!`;
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const deleteQuote = async (q) => {
    if (!window.confirm(`Delete ${q.quote_no}?`)) return;
    try {
      await api.delete(`/quotations/${q.id}`);
      setQuotes((prev) => prev.filter((x) => x.id !== q.id));
      toast.success("Deleted");
    } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-stone-950/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="quote-builder-modal" className="bg-white rounded-3xl border border-stone-200 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-20 bg-white border-b border-stone-200 px-6 py-4 flex justify-between items-start rounded-t-3xl">
          <div>
            <div className="label-eyebrow">Billing</div>
            <h2 className="font-display text-xl font-bold text-stone-900 mt-1">{submission.client_name}</h2>
            <div className="text-xs text-stone-600 mt-0.5">{submission.client_mobile}{submission.location ? ` · 📍 ${submission.location}` : ""}</div>
          </div>
          <button onClick={onClose} data-testid="quote-close" className="text-stone-500 hover:text-stone-900"><X className="w-5 h-5" /></button>
        </div>

        <div className="border-b border-stone-100 px-6 pt-3 flex gap-1">
          <TabBtn active={tab === "new"} onClick={() => setTab("new")} testId="quote-tab-new">New Quotation</TabBtn>
          <TabBtn active={tab === "list"} onClick={() => setTab("list")} testId="quote-tab-list">History ({quotes.length})</TabBtn>
        </div>

        {loading ? (
          <div className="p-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-stone-400" /></div>
        ) : tab === "new" ? (
          <NewQuoteTab
            products={products}
            items={items}
            addItem={addItem}
            removeItem={removeItem}
            updateItem={updateItem}
            discount={discount}
            setDiscount={setDiscount}
            notes={notes}
            setNotes={setNotes}
            totals={totals}
            saving={saving}
            save={save}
          />
        ) : (
          <QuoteList quotes={quotes} onDownload={downloadPdf} onWhatsApp={openWhatsApp} onDelete={deleteQuote} />
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className={`px-4 py-2.5 text-sm font-semibold border-b-2 ${active ? "border-blue-700 text-blue-700" : "border-transparent text-stone-600 hover:text-stone-900"}`}>
      {children}
    </button>
  );
}

function NewQuoteTab({ products, items, addItem, removeItem, updateItem, discount, setDiscount, notes, setNotes, totals, saving, save }) {
  if (products.length === 0) {
    return (
      <div className="p-16 text-center text-stone-500" data-testid="quote-no-products">
        No products in the catalogue yet. Ask an Admin to add products from the Products tab first.
      </div>
    );
  }
  return (
    <div className="p-6 space-y-5">
      <div className="rounded-2xl border border-stone-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 text-xs uppercase tracking-widest text-stone-600">
            <tr>
              <th className="text-left px-3 py-2.5">Product</th>
              <th className="text-right px-3 py-2.5 w-24">Qty</th>
              <th className="text-right px-3 py-2.5 w-32">Rate (₹)</th>
              <th className="text-right px-3 py-2.5 w-20">GST %</th>
              <th className="text-right px-3 py-2.5 w-32">Line Total</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-stone-500 text-xs italic">No items yet — click &ldquo;Add product&rdquo; below</td></tr>
            ) : items.map((it, idx) => {
              const p = products.find((x) => x.id === it.product_id);
              const price = it.price_override !== "" && it.price_override != null ? parseFloat(it.price_override) : (p?.price || 0);
              const line = (parseFloat(it.quantity) || 0) * price;
              return (
                <tr key={idx} data-testid={`quote-item-${idx}`} className="border-t border-stone-100">
                  <td className="px-3 py-2">
                    <select
                      value={it.product_id}
                      onChange={(e) => updateItem(idx, { product_id: e.target.value })}
                      data-testid={`quote-item-product-${idx}`}
                      className="w-full rounded-lg border border-stone-300 px-2 py-1.5 text-xs bg-white outline-none focus:ring-2 focus:ring-blue-600"
                    >
                      <option value="">— Select —</option>
                      {products.map((x) => <option key={x.id} value={x.id}>{x.sku} · {x.name} (₹{x.price}/{x.unit})</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" step="0.01" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} data-testid={`quote-item-qty-${idx}`} className="w-full text-right rounded-lg border border-stone-300 px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-blue-600" />
                  </td>
                  <td className="px-3 py-2">
                    <input type="number" step="0.01" placeholder={p ? String(p.price) : ""} value={it.price_override} onChange={(e) => updateItem(idx, { price_override: e.target.value })} data-testid={`quote-item-rate-${idx}`} className="w-full text-right rounded-lg border border-stone-300 px-2 py-1.5 text-xs outline-none focus:ring-2 focus:ring-blue-600" />
                  </td>
                  <td className="px-3 py-2 text-right text-xs text-stone-600">{p ? `${p.gst_percent}%` : "—"}</td>
                  <td className="px-3 py-2 text-right text-xs font-semibold text-stone-900">{rupee(line)}</td>
                  <td className="px-2">
                    <button onClick={() => removeItem(idx)} data-testid={`quote-item-remove-${idx}`} className="text-rose-600 hover:text-rose-700"><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <button onClick={addItem} data-testid="quote-add-item" className="w-full py-2.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 inline-flex items-center justify-center gap-1.5 border-t border-stone-100">
          <Plus className="w-3.5 h-3.5" /> Add product
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="text-xs font-semibold uppercase tracking-widest text-stone-600">Notes / terms</label>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="quote-notes" placeholder="e.g. Delivery in 3 days, 50% advance…" className="mt-1.5 w-full rounded-xl border border-stone-300 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-600" />
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
          <div className="flex justify-between text-sm text-stone-700"><span>Subtotal</span><span>{rupee(totals.sub)}</span></div>
          <div className="flex items-center justify-between text-sm text-stone-700 mt-1.5">
            <span className="inline-flex items-center gap-2">
              Discount
              <input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="quote-discount" placeholder="0" className="w-14 rounded-md border border-blue-300 px-1.5 py-0.5 text-xs text-right bg-white" />%
            </span>
            <span>- {rupee(totals.disc)}</span>
          </div>
          <div className="flex justify-between text-sm text-stone-700 mt-1.5"><span>Taxable</span><span>{rupee(totals.taxable)}</span></div>
          <div className="flex justify-between text-sm text-stone-700 mt-1"><span>GST</span><span>{rupee(totals.gst)}</span></div>
          <div className="flex justify-between text-base font-bold text-blue-800 mt-2 pt-2 border-t border-blue-300"><span>Grand Total</span><span data-testid="quote-grand-total">{rupee(totals.grand)}</span></div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={save} disabled={saving} data-testid="quote-save" className="btn-primary rounded-full px-6 py-2.5 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          Save Quotation
        </button>
      </div>
    </div>
  );
}

function QuoteList({ quotes, onDownload, onWhatsApp, onDelete }) {
  if (quotes.length === 0) return <div className="p-12 text-center text-stone-500 text-sm">No quotations yet.</div>;
  return (
    <div className="p-6 space-y-3">
      {quotes.map((q) => (
        <div key={q.id} data-testid={`quote-card-${q.id}`} className="border border-stone-200 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="min-w-0">
            <div className="font-mono text-xs font-bold text-blue-700">{q.quote_no}</div>
            <div className="text-sm text-stone-900 mt-0.5">{q.items.length} item{q.items.length === 1 ? "" : "s"} · Grand Total <strong>{rupee(q.grand_total)}</strong></div>
            <div className="text-xs text-stone-500 mt-0.5">{new Date(q.created_at).toLocaleString()} · by {q.created_by?.name || q.created_by?.email}</div>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button onClick={() => onDownload(q)} data-testid={`quote-download-${q.id}`} className="rounded-full border border-blue-600 text-blue-700 hover:bg-blue-50 px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
            <button onClick={() => onWhatsApp(q)} data-testid={`quote-wa-${q.id}`} className="rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-1.5">
              <WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp
            </button>
            <button onClick={() => onDelete(q)} data-testid={`quote-delete-${q.id}`} className="rounded-full border border-rose-200 text-rose-600 hover:bg-rose-50 px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
